import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  contestSponsors,
  sponsorshipAssignments,
  sponsorshipMetricsData,
  contestSponsorDashboard,
  tierConfigs,
  entityTypeConfig,
  visibilityConfig,
  getActiveAssignments,
  getAssignmentsByEntityType,
  getAssignmentsByStatus,
  getSponsorById,
  getMetricsBySponsorshipId,
  formatMoney,
  formatCompactNum,
  getTierConfig,
  getEntityTypeConfig,
  getVisibilityConfig,
  type SponsorTier,
  type SponsorshipEntityType,
  type ContestSponsor,
  type SponsorshipAssignment,
} from '@/mocks/rfwContestSponsors';

type TabId = 'dashboard' | 'patrocinadores' | 'asignaciones' | 'metricas' | 'tiers';

function TierBadge({ tier }: { tier: SponsorTier }) {
  const cfg = getTierConfig(tier);
  if (!cfg) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
      style={{ backgroundColor: cfg.color + '18', color: cfg.color, border: '1px solid ' + cfg.color + '30' }}
    >
      <i className={`${cfg.icon} text-[8px]`}></i>
      {cfg.label}
    </span>
  );
}

function SponsorModal({ sponsor, onClose }: { sponsor: ContestSponsor; onClose: () => void }) {
  const cfg = getTierConfig(sponsor.tier);
  const assignments = sponsorshipAssignments.filter((a) => a.sponsorId === sponsor.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors z-10 cursor-pointer"
        >
          <i className="ri-close-line"></i>
        </button>

        <div className="h-32 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <img src={sponsor.logo} alt={sponsor.name} className="w-20 h-20 object-contain relative z-10 rounded-xl bg-white/80 p-2 shadow-lg" />
        </div>

        <div className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-foreground-950">{sponsor.name}</h3>
            {cfg && <TierBadge tier={sponsor.tier} />}
          </div>

          <p className="text-xs text-foreground-600 mb-3 leading-relaxed">{sponsor.description}</p>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Sector</p>
              <p className="text-sm font-semibold text-foreground-950">{sponsor.sector}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Ubicación</p>
              <p className="text-sm font-semibold text-foreground-950">{sponsor.city}, {sponsor.country}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Inversión Total</p>
              <p className="text-sm font-extrabold text-amber-600">{formatMoney(sponsor.totalInvestment)}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Patrocinios Activos</p>
              <p className="text-sm font-extrabold text-emerald-600">{sponsor.activeSponsorships} de {sponsor.totalSponsorships}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs text-foreground-600 mb-4">
            <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-foreground-950 transition-colors">
              <i className="ri-global-line"></i> Sitio Web
            </a>
            <a href={`https://wa.me/${sponsor.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-foreground-950 transition-colors">
              <i className="ri-whatsapp-line"></i> WhatsApp
            </a>
          </div>

          {assignments.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-foreground-950 mb-2">Patrocinios ({assignments.length})</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {assignments.map((a) => {
                  const entCfg = getEntityTypeConfig(a.entityType);
                  return (
                    <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-background-100">
                      <div className="flex items-center gap-2 min-w-0">
                        {entCfg && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${entCfg.color} ${entCfg.textColor}`}>
                            <i className={`${entCfg.icon} text-[8px] mr-0.5`}></i>
                            {entCfg.label}
                          </span>
                        )}
                        <span className="text-xs text-foreground-800 truncate">{a.entityName}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-amber-600 font-semibold">{formatMoney(a.investment)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${a.status === 'activo' ? 'bg-emerald-50 text-emerald-600' : a.status === 'programado' ? 'bg-sky-50 text-sky-600' : 'bg-foreground-100 text-foreground-500'}`}>
                          {a.status === 'activo' ? 'Activo' : a.status === 'programado' ? 'Prog.' : 'Finalizado'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignmentModal({ assignment, onClose }: { assignment: SponsorshipAssignment; onClose: () => void }) {
  const metrics = getMetricsBySponsorshipId(assignment.id);
  const entCfg = getEntityTypeConfig(assignment.entityType);
  const tierCfg = getTierConfig(assignment.tier);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-md bg-background-50 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors z-10 cursor-pointer">
          <i className="ri-close-line"></i>
        </button>

        <div className="h-24 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 flex items-center justify-center">
          <div className="flex items-center gap-4">
            <img src={assignment.sponsorLogo} alt={assignment.sponsorName} className="w-14 h-14 object-contain rounded-xl bg-white/80 p-1.5 shadow-md" />
            {entCfg && (
              <div className={`w-14 h-14 rounded-xl ${entCfg.color} flex items-center justify-center`}>
                <i className={`${entCfg.icon} text-2xl ${entCfg.textColor}`}></i>
              </div>
            )}
          </div>
        </div>

        <div className="p-5">
          <h3 className="text-base font-bold text-foreground-950 mb-1">{assignment.sponsorName}</h3>
          <p className="text-sm text-foreground-700 font-medium mb-0.5">{assignment.entityName}</p>
          <div className="flex items-center gap-2 mb-3">
            {entCfg && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${entCfg.color} ${entCfg.textColor}`}>
                <i className={`${entCfg.icon} text-[8px] mr-0.5`}></i>
                {entCfg.label}
              </span>
            )}
            {tierCfg && <TierBadge tier={assignment.tier} />}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${assignment.status === 'activo' ? 'bg-emerald-50 text-emerald-600' : assignment.status === 'programado' ? 'bg-sky-50 text-sky-600' : 'bg-foreground-100 text-foreground-500'}`}>
              {assignment.status === 'activo' ? 'Activo' : assignment.status === 'programado' ? 'Programado' : 'Finalizado'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Radio</p>
              <p className="text-sm font-semibold text-foreground-950">{assignment.radioName}</p>
              <p className="text-[10px] text-foreground-500">{assignment.radioFrequency}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Inversión</p>
              <p className="text-sm font-extrabold text-amber-600">{formatMoney(assignment.investment)}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Período</p>
              <p className="text-xs font-semibold text-foreground-950">{assignment.startDate}</p>
              <p className="text-xs text-foreground-500">hasta {assignment.endDate}</p>
            </div>
            <div className="rounded-lg bg-background-100 p-3">
              <p className="text-[10px] text-foreground-500">Visibilidad</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {assignment.visibility.map((v) => {
                  const vCfg = getVisibilityConfig(v);
                  if (!vCfg) return null;
                  return (
                    <span key={v} className={`text-[9px] px-1 py-0.5 rounded-full font-medium ${vCfg.color} ${vCfg.textColor}`}>
                      {vCfg.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {metrics && (
            <div className="rounded-xl bg-background-100 p-4">
              <p className="text-xs font-semibold text-foreground-950 mb-3">Métricas · {metrics.periodLabel}</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-lg font-extrabold text-foreground-800">{formatCompactNum(metrics.impressions)}</p>
                  <p className="text-[10px] text-foreground-500">Impresiones</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-extrabold text-emerald-600">{metrics.ctr}%</p>
                  <p className="text-[10px] text-foreground-500">CTR</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-extrabold text-emerald-600">{metrics.conversionRate}%</p>
                  <p className="text-[10px] text-foreground-500">Conversión</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-extrabold text-foreground-800">{formatCompactNum(metrics.clicks)}</p>
                  <p className="text-[10px] text-foreground-500">Clicks</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-extrabold text-foreground-800">{formatCompactNum(metrics.participations)}</p>
                  <p className="text-[10px] text-foreground-500">Particip.</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-extrabold text-foreground-800">{formatCompactNum(metrics.conversions)}</p>
                  <p className="text-[10px] text-foreground-500">Conv.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PatrocinadoresPage() {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [sponsorFilter, setSponsorFilter] = useState<SponsorTier | 'todos'>('todos');
  const [entityFilter, setEntityFilter] = useState<SponsorshipEntityType | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<'activo' | 'finalizado' | 'programado' | 'todos'>('todos');
  const [selectedSponsor, setSelectedSponsor] = useState<ContestSponsor | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<SponsorshipAssignment | null>(null);

  const csd = contestSponsorDashboard;
  const activeAssignments = getActiveAssignments();

  const filteredSponsors = contestSponsors.filter((s) => {
    if (sponsorFilter !== 'todos' && s.tier !== sponsorFilter) return false;
    return true;
  });

  const filteredAssignments = (() => {
    let list = sponsorshipAssignments;
    if (entityFilter !== 'todos') list = getAssignmentsByEntityType(entityFilter);
    if (statusFilter !== 'todos') list = list.filter((a) => a.status === statusFilter);
    return list;
  })();

  const tabs = [
    { id: 'dashboard' as TabId, label: 'Dashboard', icon: 'ri-dashboard-line' },
    { id: 'patrocinadores' as TabId, label: 'Patrocinadores', icon: 'ri-hand-heart-line', count: contestSponsors.length },
    { id: 'asignaciones' as TabId, label: 'Asignaciones', icon: 'ri-links-line', count: sponsorshipAssignments.length },
    { id: 'metricas' as TabId, label: 'Métricas', icon: 'ri-file-chart-line' },
    { id: 'tiers' as TabId, label: 'Niveles', icon: 'ri-stack-line', count: tierConfigs.length },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-amber-50 via-background-50 to-emerald-50">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-emerald-400/8 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-amber-200 text-amber-700 text-xs font-semibold mb-6 shadow-sm">
            <i className="ri-hand-heart-line text-xs"></i>
            BLOQUE 15 · PATROCINADORES DE CONCURSOS Y EVENTOS
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 tracking-tight leading-[1.1] mb-4">
            Monetizá tus{' '}
            <span className="text-amber-600">Concursos y Eventos</span>
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-8">
            Las radios venden sus propios patrocinadores. Elegís el nivel, gestionás la visibilidad, medís resultados. RFW no interviene — 100% de la ganancia es tuya.
          </p>

          {/* Hero Stats */}
          <div className="inline-flex flex-wrap items-center justify-center gap-4 md:gap-8">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-amber-600 tabular-nums">{csd.activeSponsors}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Patrocinadores Activos</p>
            </div>
            <div className="w-px h-8 bg-amber-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-emerald-600 tabular-nums">{csd.activeCampaigns}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Patrocinios Activos</p>
            </div>
            <div className="w-px h-8 bg-amber-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-foreground-950 tabular-nums">{formatMoney(csd.totalRevenue)}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Ingresos Totales</p>
            </div>
            <div className="w-px h-8 bg-amber-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-emerald-600 tabular-nums">{csd.avgCtr}%</p>
              <p className="text-[10px] text-foreground-500 font-medium">CTR Promedio</p>
            </div>
            <div className="w-px h-8 bg-amber-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-foreground-950 tabular-nums">{csd.avgConversion}%</p>
              <p className="text-[10px] text-foreground-500 font-medium">Conversión</p>
            </div>
            <div className="w-px h-8 bg-amber-200 hidden sm:block"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalImpressions)}</p>
              <p className="text-[10px] text-foreground-500 font-medium">Impresiones</p>
            </div>
          </div>
        </div>
      </section>

      {/* 100% Radio Banner */}
      <section className="bg-emerald-600">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-check-double-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm md:text-base">100% Radio. RFW No Interviene en la Venta.</p>
              <p className="text-emerald-100 text-xs">Vos vendés. Vos cobrás. Vos gestionás. Nosotros mostramos las métricas.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-white/80 text-xs">
            <span className="flex items-center gap-1"><i className="ri-bank-line"></i> Ganancia 100% Radio</span>
            <span className="hidden sm:block">·</span>
            <span className="flex items-center gap-1 hidden sm:flex"><i className="ri-shield-check-line"></i> Sin comisiones RFW</span>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto py-10">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className="ri-hand-heart-line text-amber-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-amber-600 tabular-nums">{csd.activeSponsors}</p>
                <p className="text-xs text-foreground-500 mt-1">Patrocinadores Activos</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-emerald-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{csd.activeCampaigns}</p>
                <p className="text-xs text-foreground-500 mt-1">Patrocinios Activos</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-amber-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatMoney(csd.totalRevenue)}</p>
                <p className="text-xs text-foreground-500 mt-1">Ingresos Totales</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-rose-100 flex items-center justify-center">
                  <i className="ri-eye-line text-rose-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalImpressions)}</p>
                <p className="text-xs text-foreground-500 mt-1">Impresiones Totales</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-purple-100 flex items-center justify-center">
                  <i className="ri-line-chart-line text-purple-600"></i>
                </div>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{csd.avgCtr}%</p>
                <p className="text-xs text-foreground-500 mt-1">CTR Promedio</p>
              </div>
            </div>

            {/* Second row KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-cursor-line text-sky-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalClicks)}</p>
                  <p className="text-[10px] text-foreground-500">Clicks Totales</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-heart-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalParticipations)}</p>
                  <p className="text-[10px] text-foreground-500">Participaciones</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-double-line text-emerald-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{csd.avgConversion}%</p>
                  <p className="text-[10px] text-foreground-500">Conversión Promedio</p>
                </div>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-radio-line text-emerald-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-lg font-extrabold text-foreground-950 tabular-nums">{csd.radioRevenuePercent}%</p>
                  <p className="text-[10px] text-foreground-500">Ganancia Radio</p>
                </div>
              </div>
            </div>

            {/* Entity breakdown */}
            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
              <h3 className="text-sm font-bold text-foreground-950 mb-4">Patrocinios por Tipo de Entidad</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {entityTypeConfig.map((et) => {
                  const assignments = getAssignmentsByEntityType(et.id);
                  const active = assignments.filter((a) => a.status === 'activo');
                  const totalInv = assignments.reduce((sum, a) => sum + a.investment, 0);
                  return (
                    <div key={et.id} className="rounded-xl bg-background-50 p-4 text-center border border-background-200/70 hover:border-amber-300/50 transition-colors cursor-pointer">
                      <div className={`w-10 h-10 mx-auto mb-2 rounded-xl ${et.color} flex items-center justify-center`}>
                        <i className={`${et.icon} text-lg ${et.textColor}`}></i>
                      </div>
                      <p className="text-xs font-bold text-foreground-950">{et.label}</p>
                      <p className="text-lg font-extrabold text-amber-600 tabular-nums mt-1">{formatMoney(totalInv)}</p>
                      <p className="text-[10px] text-foreground-500">{active.length} activo{active.length !== 1 ? 's' : ''} de {assignments.length}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link
                to="/competencias"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-sword-line"></i>
                Ver Competencias Activas
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}

        {/* Patrocinadores Tab */}
        {activeTab === 'patrocinadores' && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="text-xs text-foreground-500 mr-1">Filtrar:</span>
              {([{ id: 'todos', label: 'Todos' }, ...tierConfigs.map((t) => ({ id: t.id, label: t.label }))] as { id: SponsorTier | 'todos'; label: string }[]).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSponsorFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    sponsorFilter === f.id
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <p className="text-xs text-foreground-500 mb-4">{filteredSponsors.length} patrocinadores</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredSponsors.map((s) => {
                const cfg = getTierConfig(s.tier);
                return (
                  <div
                    key={s.id}
                    onClick={() => setSelectedSponsor(s)}
                    className="rounded-xl bg-background-100 border border-background-200/70 p-4 hover:border-amber-300/50 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={s.logo} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-bold text-foreground-950 truncate">{s.name}</h4>
                        <p className="text-[10px] text-foreground-500">{s.sector} · {s.city}</p>
                      </div>
                    </div>
                    {cfg && <TierBadge tier={s.tier} />}
                    <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">{s.description}</p>
                    <div className="mt-3 pt-3 border-t border-background-200/70 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] text-foreground-500">Inversión</p>
                        <p className="text-sm font-extrabold text-amber-600">{formatMoney(s.totalInvestment)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-foreground-500">Patrocinios</p>
                        <p className="text-sm font-extrabold text-emerald-600">{s.activeSponsorships} / {s.totalSponsorships}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Asignaciones Tab */}
        {activeTab === 'asignaciones' && (
          <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">Entidad:</span>
                {([{ id: 'todos', label: 'Todas' }, ...entityTypeConfig] as { id: SponsorshipEntityType | 'todos'; label: string }[]).map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setEntityFilter(f.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                      entityFilter === f.id
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">Estado:</span>
                {(['todos', 'activo', 'programado', 'finalizado'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all duration-200 whitespace-nowrap ${
                      statusFilter === s
                        ? (s === 'activo' ? 'bg-emerald-500' : s === 'programado' ? 'bg-sky-500' : s === 'finalizado' ? 'bg-foreground-500' : 'bg-amber-500') + ' text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-200/70'
                    }`}
                  >
                    {s === 'todos' ? 'Todos' : s}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-4">{filteredAssignments.length} asignaciones</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAssignments.map((a) => {
                const entCfg = getEntityTypeConfig(a.entityType);
                const tierCfg = getTierConfig(a.tier);
                const metrics = getMetricsBySponsorshipId(a.id);
                return (
                  <div
                    key={a.id}
                    onClick={() => setSelectedAssignment(a)}
                    className="rounded-xl bg-background-100 border border-background-200/70 p-4 hover:border-amber-300/50 hover:shadow-md transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5 mb-3">
                      <img src={a.sponsorLogo} alt={a.sponsorName} className="w-8 h-8 rounded-lg object-cover flex-shrink-0 bg-background-200" loading="lazy" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-foreground-950 truncate">{a.sponsorName}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {entCfg && (
                            <span className={`text-[9px] px-1 py-0.5 rounded-full font-medium ${entCfg.color} ${entCfg.textColor}`}>
                              <i className={`${entCfg.icon} text-[7px] mr-0.5`}></i>
                              {entCfg.label}
                            </span>
                          )}
                          {tierCfg && <TierBadge tier={a.tier} />}
                        </div>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        a.status === 'activo' ? 'bg-emerald-50 text-emerald-600' : a.status === 'programado' ? 'bg-sky-50 text-sky-600' : 'bg-foreground-100 text-foreground-500'
                      }`}>
                        {a.status === 'activo' ? 'Activo' : a.status === 'programado' ? 'Prog.' : 'Fin.'}
                      </span>
                    </div>

                    <p className="text-sm font-semibold text-foreground-800 mb-1">{a.entityName}</p>
                    <p className="text-xs text-foreground-500">{a.radioName} {a.radioFrequency}</p>

                    {/* Visibility pills */}
                    <div className="flex flex-wrap gap-1 mt-2 mb-3">
                      {a.visibility.map((v) => {
                        const vCfg = getVisibilityConfig(v);
                        if (!vCfg) return null;
                        return (
                          <span key={v} className={`text-[9px] px-1 py-0.5 rounded-full ${vCfg.color} ${vCfg.textColor}`}>
                            {vCfg.label}
                          </span>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between pt-2.5 border-t border-background-200/70">
                      <span className="text-sm font-extrabold text-amber-600">{formatMoney(a.investment)}</span>
                      {metrics && (
                        <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                          <span>{formatCompactNum(metrics.impressions)} impr.</span>
                          <span>{metrics.ctr}% CTR</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Métricas Tab */}
        {activeTab === 'metricas' && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <p className="text-xs text-foreground-500 mb-1">Impresiones Totales</p>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalImpressions)}</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <p className="text-xs text-foreground-500 mb-1">Clicks Totales</p>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalClicks)}</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <p className="text-xs text-foreground-500 mb-1">Participaciones</p>
                <p className="text-2xl font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(csd.totalParticipations)}</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <p className="text-xs text-foreground-500 mb-1">CTR Promedio</p>
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{csd.avgCtr}%</p>
              </div>
              <div className="rounded-2xl bg-background-100 p-5 text-center border border-background-200/70">
                <p className="text-xs text-foreground-500 mb-1">Conversión Promedio</p>
                <p className="text-2xl font-extrabold text-emerald-600 tabular-nums">{csd.avgConversion}%</p>
              </div>
            </div>

            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
              <h3 className="text-sm font-bold text-foreground-950 mb-5">Métricas por Patrocinio Activo</h3>
              <div className="space-y-3">
                {activeAssignments.map((a) => {
                  const metrics = getMetricsBySponsorshipId(a.id);
                  if (!metrics) return null;
                  return (
                    <div key={a.id} className="rounded-xl bg-background-50 p-4 border border-background-200/70">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <img src={a.sponsorLogo} alt={a.sponsorName} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                          <span className="text-xs font-bold text-foreground-950">{a.sponsorName}</span>
                        </div>
                        <span className="text-[10px] text-foreground-500">{metrics.periodLabel}</span>
                      </div>
                      <p className="text-xs text-foreground-600 mb-2.5">{a.entityName} · {a.radioName}</p>
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                        <div className="text-center">
                          <p className="text-sm font-extrabold text-foreground-800">{formatCompactNum(metrics.impressions)}</p>
                          <p className="text-[9px] text-foreground-500">Impr.</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-extrabold text-emerald-600">{metrics.ctr}%</p>
                          <p className="text-[9px] text-foreground-500">CTR</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-extrabold text-emerald-600">{metrics.conversionRate}%</p>
                          <p className="text-[9px] text-foreground-500">Conv.</p>
                        </div>
                        <div className="text-center hidden md:block">
                          <p className="text-sm font-extrabold text-foreground-800">{formatCompactNum(metrics.clicks)}</p>
                          <p className="text-[9px] text-foreground-500">Clicks</p>
                        </div>
                        <div className="text-center hidden md:block">
                          <p className="text-sm font-extrabold text-foreground-800">{formatCompactNum(metrics.participations)}</p>
                          <p className="text-[9px] text-foreground-500">Particip.</p>
                        </div>
                      </div>
                      {/* Mini bar */}
                      <div className="mt-3 w-full h-2 rounded-full bg-background-200 overflow-hidden flex">
                        <div className="h-full bg-amber-500 rounded-l-full" style={{ width: `${Math.min(metrics.ctr * 5, 100)}%` }}></div>
                        <div className="h-full bg-emerald-400" style={{ width: `${Math.min(metrics.conversionRate * 5, 100)}%` }}></div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[8px] text-amber-500">CTR {metrics.ctr}%</span>
                        <span className="text-[8px] text-emerald-500">Conv. {metrics.conversionRate}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tiers Tab */}
        {activeTab === 'tiers' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tierConfigs.map((t) => (
                <div
                  key={t.id}
                  className="rounded-2xl bg-background-100 border border-background-200/70 p-6 hover:shadow-md transition-all duration-200 cursor-pointer"
                  style={{ borderTop: `3px solid ${t.color}` }}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.color + '15' }}>
                      <i className={`${t.icon} text-xl`} style={{ color: t.color }}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground-950">{t.label}</h3>
                      <p className="text-sm font-semibold" style={{ color: t.color }}>{t.priceRange}</p>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">{t.description}</p>
                  <div className="mt-4 pt-4 border-t border-background-200/70">
                    <p className="text-[10px] font-semibold text-foreground-500 mb-2">Patrocinadores en este nivel</p>
                    <div className="space-y-1.5">
                      {contestSponsors.filter((s) => s.tier === t.id).slice(0, 4).map((s) => (
                        <div key={s.id} className="flex items-center gap-2 text-xs">
                          <div className="w-5 h-5 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                            <img src={s.logo} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          <span className="text-foreground-800 truncate">{s.name}</span>
                          <span className="text-amber-600 font-semibold ml-auto whitespace-nowrap">{formatMoney(s.totalInvestment)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Visibilidad locations */}
            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6">
              <h3 className="text-sm font-bold text-foreground-950 mb-4">Donde Aparecen los Patrocinadores</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {visibilityConfig.map((v) => (
                  <div key={v.id} className="rounded-xl bg-background-50 p-4 text-center border border-background-200/70">
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-xl ${v.color} flex items-center justify-center`}>
                      <i className={`${v.icon} text-lg ${v.textColor}`}></i>
                    </div>
                    <p className="text-xs font-bold text-foreground-950">{v.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <section className="bg-background-100 border-t border-background-200/70 py-12">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
            <i className="ri-check-double-line text-xs"></i>
            REGLA FUNDAMENTAL
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            La radio puede vender sus propios patrocinadores.
          </h2>
          <p className="text-foreground-600 text-sm max-w-xl mx-auto mb-6">
            RFW no interviene. 100% Radio. Las métricas son públicas para que el patrocinador vea resultados, pero la venta, el cobro y la gestión son completamente tuyos.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/publicidad" className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 border border-background-200/70 hover:border-amber-300 text-foreground-800 text-xs font-semibold rounded-xl transition-all duration-200 whitespace-nowrap">
              <i className="ri-megaphone-line"></i>
              Publicidad General
            </Link>
            <Link to="/competencias" className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-xl transition-all duration-200 whitespace-nowrap">
              <i className="ri-sword-line"></i>
              Competencias Activas
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Modals */}
      {selectedSponsor && (
        <SponsorModal sponsor={selectedSponsor} onClose={() => setSelectedSponsor(null)} />
      )}
      {selectedAssignment && (
        <AssignmentModal assignment={selectedAssignment} onClose={() => setSelectedAssignment(null)} />
      )}
    </div>
  );
}